import React, { useId } from "react";
import { FormControlWrapper } from "../FormControlWrapper";
import { FormControlOutline } from "../FormControlWrapper/FormControlOutline";
import { FormControlLabel } from "../FormControlWrapper/FormControlLabel";
import { splitFormControlWrapperProps } from "../FormControlWrapper/helpers";
import { cn } from "@gettactic/helpers";
import {
  RadioInputProps,
  RadioInputPropsWithFormControl
} from "./RadioInput.types";

const radioHoverClassBase = "outline outline-[3px] outline-neutral-50";
const radioHoverClass = radioHoverClassBase
  .split(" ")
  .map((c) => `hover:${c} group-hover:${c}`)
  .join(" ");

const RadioInputInput = React.forwardRef<HTMLInputElement, RadioInputProps>(
  (props, ref) => {
    const {
      checked,
      onChange,
      disableOutline,
      radioPosition,
      className,
      disabled,
      ...rest
    } = props;

    return (
      <input
        ref={ref}
        type="radio"
        checked={checked}
        onChange={(e) => onChange?.(e.target.checked)}
        disabled={disabled}
        className={cn(
          "h-4 w-4 shrink-0 appearance-none rounded-full",
          "border border-neutral-400 bg-white",
          "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-black",
          "disabled:cursor-not-allowed disabled:opacity-50",
          "checked:border-[5px] checked:border-black",
          radioHoverClass,
          className
        )}
        {...rest}
      />
    );
  }
);

RadioInputInput.displayName = "RadioInputInput";

/**
 * One option of a single-choice question, with label and description. Render one per option;
 * give them the same `name` so they form a group (arrow keys move between them). Inside
 * react-hook-form use `HookFormRadioInput`, which sets the name for you.
 */
export const RadioInput = React.forwardRef<
  HTMLInputElement,
  RadioInputPropsWithFormControl
>((props, ref) => {
  const { radioPosition = "bottom", ...restProps } = props;
  const [wrapperProps, inputProps] = splitFormControlWrapperProps(restProps);
  const {
    label,
    description,
    info,
    isRequired,
    isOptional,
    ...restWrapperProps
  } = wrapperProps;
  const { checked, onChange, disabled } = inputProps;
  // One id shared by the radio and its label: the label then names the radio and selects it
  // natively, with no extra click handling
  const generatedId = useId();
  const radioId = restWrapperProps.id ?? generatedId;

  // The description is not a label (it would lengthen the name); clicking it still selects
  const handleDescriptionClick = () => {
    if (!disabled && onChange && !checked) {
      onChange(true);
    }
  };

  // For left/right positions, we need a custom layout
  if (radioPosition === "left" || radioPosition === "right") {
    const radio = (
      <RadioInputInput
        ref={ref}
        {...inputProps}
        id={radioId}
        radioPosition={radioPosition}
      />
    );

    return (
      <div className="w-full">
        <div className="flex items-start justify-between gap-4">
          {radioPosition === "left" && (
            <div className="flex shrink-0 items-center pt-0.5">{radio}</div>
          )}
          <div className="flex min-w-0 flex-1 flex-col">
            {label && (
              <FormControlLabel
                htmlFor={radioId}
                isRequired={isRequired}
                isOptional={isOptional}
                info={info}
                className={cn(!disabled && "cursor-pointer")}
              >
                {label}
              </FormControlLabel>
            )}
            {description && (
              <p
                className={cn(
                  "text-md mt-1 text-justify text-content",
                  !disabled && onChange && "cursor-pointer"
                )}
                onClick={handleDescriptionClick}
              >
                {description}
              </p>
            )}
          </div>
          {radioPosition === "right" && (
            <div className="flex w-12 shrink-0 items-center justify-end pt-0.5">
              {radio}
            </div>
          )}
        </div>
        {inputProps.disableOutline !== true && <FormControlOutline />}
      </div>
    );
  }

  // Default bottom position - use FormControlWrapper
  return (
    <FormControlWrapper
      variant={"unstyled"}
      {...restWrapperProps}
      id={radioId}
      label={
        label ? (
          <span className={cn("w-full", !disabled && "cursor-pointer")}>
            {label}
          </span>
        ) : undefined
      }
      description={
        description ? (
          <div
            className={cn(!disabled && onChange && "cursor-pointer")}
            onClick={handleDescriptionClick}
          >
            {description}
          </div>
        ) : undefined
      }
      info={info}
      isRequired={isRequired}
      isOptional={isOptional}
    >
      <RadioInputInput
        ref={ref}
        {...inputProps}
        radioPosition={radioPosition}
        id={radioId}
      />
      {inputProps.disableOutline !== true && <FormControlOutline />}
    </FormControlWrapper>
  );
});

RadioInput.displayName = "RadioInput";
